import * as React from 'react';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import {
  defaultApps,
  platformApp,
  filterAppsByPermissions,
  type ZafroiApp,
  type ZafroiAppId,
} from '../../config/apps';
import { cn } from '../../lib/utils';

/**
 * Single source of truth for which apps a viewer sees in the launcher.
 *
 * Everyone gets the product apps (`defaultApps`: operations/engage/workforce/kosh).
 * A TENANT_ADMIN additionally gets the Platform tile to reach their OWN-tenant
 * account/self-service area (subscription, members, roles, security) — which is
 * tenant-scoped, not control-plane. A PLATFORM_ADMIN is NOT given a product
 * switcher: their home is the control plane (platform.zafroi.com) and the
 * product surfaces redirect them there (ADR-024 audience/scope plane split).
 *
 * Centralising this here means operations/engage/workforce/kosh can't drift — they
 * all render `<AppSwitcher tier={me.tier} … />` and get the identical list.
 */
export function appsForTier(tier?: string): ZafroiApp[] {
  return tier === 'TENANT_ADMIN' ? [...defaultApps, platformApp] : defaultApps;
}

export interface AppSwitcherProps {
  /** Override the resolved registry outright (skips the tier-based default). */
  apps?: ZafroiApp[];
  /** Viewer tier. When `apps` is omitted, drives the persona-aware default
   *  list via {@link appsForTier} (tenant admins also see the Platform app). */
  tier?: string;
  /** Per-user product access (ADR-028): the viewer's effective permission keys.
   *  When provided, product tiles are filtered to those the user has ≥1
   *  permission in (the current app always stays). Omit to skip filtering. */
  permissions?: string[] | null;
  /** The app currently in focus — highlighted in the launcher. */
  currentAppId?: ZafroiAppId;
  /** Invoked when a tile is clicked. If returns `false`, navigation is cancelled. */
  onAppSelect?: (app: ZafroiApp) => void | false;
  /** Custom trigger renderer. Defaults to a graphite "Operations / All apps" pill. */
  renderTrigger?: (state: {
    currentApp?: ZafroiApp;
    open: boolean;
  }) => React.ReactNode;
  className?: string;
}

/**
 * AppSwitcher — the ZAFROI ecosystem launcher.
 *
 * Renders a popover panel that lists every app in the ecosystem as a tile
 * (icon, name, description, status). The current app is highlighted; tiles
 * with status `coming-soon` render disabled. The trigger button is fully
 * customizable via `renderTrigger`.
 */
export function AppSwitcher({
  apps,
  tier,
  permissions,
  currentAppId,
  onAppSelect,
  renderTrigger,
  className,
}: AppSwitcherProps) {
  const [open, setOpen] = React.useState(false);
  // Explicit `apps` wins; otherwise derive from the viewer's tier.
  const baseApps = apps ?? appsForTier(tier);
  // Per-user product access (ADR-028): hide tiles the user has no permissions
  // for — but never hide the app they're currently in.
  const resolvedApps = React.useMemo(() => {
    if (permissions === undefined) return baseApps;
    const filtered = filterAppsByPermissions(baseApps, permissions, tier);
    if (currentAppId && !filtered.some((a) => a.id === currentAppId)) {
      const cur = baseApps.find((a) => a.id === currentAppId);
      if (cur) return [cur, ...filtered];
    }
    return filtered;
  }, [baseApps, permissions, tier, currentAppId]);
  const currentApp = currentAppId
    ? resolvedApps.find((app) => app.id === currentAppId)
    : undefined;

  const handleSelect = (app: ZafroiApp, event: React.MouseEvent) => {
    if (app.status === 'coming-soon') {
      event.preventDefault();
      return;
    }
    const result = onAppSelect?.(app);
    if (result === false) {
      event.preventDefault();
      return;
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          (renderTrigger({ currentApp, open }) as React.ReactElement)
        ) : (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-sm font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
              className,
            )}
            aria-label="Switch ZAFROI app"
          >
            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
            <span className="hidden sm:inline">
              {currentApp?.name ?? 'All apps'}
            </span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
            />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={10}
        className="w-[min(92vw,560px)] p-0"
      >
        <div className="border-b border-border px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            ZAFROI ecosystem
          </p>
          <p className="mt-1 text-sm text-foreground">
            Jump between the apps connected to your workspace.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2">
          {resolvedApps.map((app) => {
            const Icon = app.icon;
            const isActive = app.id === currentAppId;
            const disabled = app.status === 'coming-soon';
            return (
              <a
                key={app.id}
                href={app.url}
                onClick={(event) => handleSelect(app, event)}
                aria-current={isActive ? 'page' : undefined}
                aria-disabled={disabled || undefined}
                className={cn(
                  'group relative flex flex-col gap-1.5 rounded-md border border-border bg-card p-3 text-left transition-all',
                  'hover:border-foreground/20 hover:bg-muted/50 hover:shadow-elev-1',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                  isActive && 'border-primary/40 bg-primary/5 shadow-elev-1',
                  disabled && 'pointer-events-none opacity-60',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-md bg-muted transition-colors group-hover:bg-background',
                      app.accentClassName,
                    )}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  {app.status === 'beta' && (
                    <Badge tone="primary" withDot={false}>
                      BETA
                    </Badge>
                  )}
                  {app.status === 'coming-soon' && (
                    <Badge tone="neutral" withDot={false}>
                      Soon
                    </Badge>
                  )}
                  {isActive && (
                    <span className="text-[11px] font-medium uppercase tracking-wider text-primary">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {app.name}
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  {app.description}
                </p>
              </a>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
